import * as React from 'react';

import { ParsedCompoundUnit, StringifiedCompoundUnit } from '@acceligentllc/shared/utils/unit';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { CompoundUnitEnum, CompoundUnitValueLimits } from '@acceligentllc/shared/enums/quantityUnit';

import Dropdown from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';
import { ComparatorLabelMap, ComparatorType } from '../../types';
import CompoundUnitField from './NumericFieldFilter/CompoundUnitField';
import NonCompoundField from './NumericFieldFilter/NonCompoundField';

const RANGE_OPTIONS = ['min', 'max'] as const;

type ComparatorOption = {
	id: ComparatorType;
	label: string;
};

const COMPARATOR_OPTIONS: ComparatorOption[] =
	Object.keys(ComparatorType).map((_comparator) => ({ id: _comparator as ComparatorType, label: ComparatorLabelMap[_comparator] as string }));

type WithRangeProps = {
	isRange: true;
	value: { min: Nullable<number>; max: Nullable<number>; };
	setValue: (_newValue: { min: Nullable<number>; max: Nullable<number>; }) => void;
};

type NonRangeProps = {
	isRange: false;
	value: Nullable<number>;
	setValue: (newValue: Nullable<number>) => void;
};

export type RangeProps = WithRangeProps | NonRangeProps;

type Props = {
	comparator: Nullable<ComparatorType>;
	setComparator: (comparator: ComparatorType) => void;
	unit: Nullable<QuantityUnitType>;
	name: string;
} & RangeProps;
type CompoundUnitValueRangeType = Record<typeof RANGE_OPTIONS[number], Record<string, Nullable<number>>>;

const _initializeCompoundUnitValue = (value: Nullable<number>, unit: Nullable<QuantityUnitType>) => {
	if (value === null || unit === null || !CompoundUnitEnum[unit]) {
		return null;
	}

	const parsedValue = ParsedCompoundUnit[unit](value);
	return parsedValue;
};

const NumericFieldFilter: React.FC<Props> = (props) => {
	const { value, setValue, comparator, setComparator, unit, name, isRange } = props;

	const [compoundUnitValue, setCompoundUnitValue] = React.useState<Record<string, Nullable<number>>>(typeof value === 'number' ? _initializeCompoundUnitValue(value, unit) : null); // key = singular compound unit, e.g. LFT from LFT_LIN
	const [compoundUnitValueRange, setCompoundUnitValueRange] = React.useState<CompoundUnitValueRangeType>(
		(typeof value === 'object' &&
			value !== null &&
			'min' in value &&
			'max' in value)
			? {
				min: _initializeCompoundUnitValue(value.min, unit),
				max: _initializeCompoundUnitValue(value.max, unit),
			}
			: {
				min: null,
				max: null,
			}
	);

	const comparatorValue = React.useMemo(() => COMPARATOR_OPTIONS.find((_option) => _option.id === comparator), [comparator]);

	const onNonRangeValueChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (isRange) {
			return;
		}
		const newValue: Nullable<number> = event.target.value !== '' ? +event.target.value : null;
		setValue(newValue);
	}, [isRange, setValue]);

	const onRangeValueChange = React.useCallback((rangeOption: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!isRange) {
			return;
		}
		const newValue: Nullable<number> = event.target.value !== '' ? +event.target.value : null;
		setValue({
			...value,
			[rangeOption]: newValue,
		});
	}, [isRange, setValue, value]);

	const onComparatorChange = React.useCallback((option: ComparatorOption) => {
		setComparator(ComparatorType[option.id]);
	}, [setComparator]);

	const normalizeFieldValue = React.useCallback((min: number, max: number) => {
		return (_value: number) => {
			if (_value < min) {
				return min;
			} else if (_value > max) {
				return max;
			} else {
				return _value;
			}
		};
	}, []);

	const onSingularCompoundUnitValueChange = React.useCallback((
		compoundUnit: string,
		rangeOption?: typeof RANGE_OPTIONS[number]
	) => (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!unit) {
			return;
		}

		const limits = CompoundUnitValueLimits[unit][compoundUnit];

		const newValue: Nullable<number> = event.target.value !== ''
			? normalizeFieldValue(limits.min, limits.max)(+event.target.value)
			: null;

		if (isRange) {
			if (!rangeOption) {
				return;
			}
			const rangeValue = compoundUnitValueRange?.[rangeOption] ?? {};

			const newCompundUnitValue = {
				...compoundUnitValueRange,
				[rangeOption]: {
					...rangeValue,
					[compoundUnit]: newValue,
				},
			} as typeof compoundUnitValueRange;

			setCompoundUnitValueRange(newCompundUnitValue);
			const plainUnitMin = StringifiedCompoundUnit[unit](newCompundUnitValue?.min);
			const plainUnitMax = StringifiedCompoundUnit[unit](newCompundUnitValue?.max);

			setValue({
				min: plainUnitMin ? +plainUnitMin : null,
				max: plainUnitMax ? +plainUnitMax : null,
			});
		} else {
			const newCompundUnitValue = {
				...compoundUnitValue,
				[compoundUnit]: newValue,
			};
			const plainUnit = StringifiedCompoundUnit[unit](newCompundUnitValue);

			setValue(plainUnit !== null ? +plainUnit : null);
			setCompoundUnitValue(newCompundUnitValue);
		}

	}, [compoundUnitValue, compoundUnitValueRange, isRange, normalizeFieldValue, setValue, unit]);

	const renderSingularCompoundUnit = React.useCallback((compoundUnit: string, rangeOption?: typeof RANGE_OPTIONS[number]) => {
		const _value = rangeOption ? compoundUnitValueRange[rangeOption]?.[compoundUnit] : compoundUnitValue?.[compoundUnit];
		if (!unit) {
			return null;
		}

		return (
			<CompoundUnitField
				compoundUnit={compoundUnit}
				name={name}
				onSingularCompoundUnitValueChange={onSingularCompoundUnitValueChange(compoundUnit, rangeOption)}
				unit={unit}
				value={_value}
			/>
		);
	}, [compoundUnitValue, compoundUnitValueRange, name, onSingularCompoundUnitValueChange, unit]);

	const renderValue = React.useCallback(() => {
		const isCompoundUnit = unit && CompoundUnitEnum[unit];
		if (isRange && !isCompoundUnit) {
			return (
				<>
					<NonCompoundField
						label="Min"
						name={name}
						onValueChange={onRangeValueChange('min')}
						unit={unit}
						value={value.min}
					/>
					<NonCompoundField
						label="Max"
						name={name}
						onValueChange={onRangeValueChange('max')}
						unit={unit}
						value={value.max}
					/>
				</>
			);
		}

		if (isRange && isCompoundUnit) {
			const elements = Object.keys(CompoundUnitValueLimits[unit]);
			return RANGE_OPTIONS.map((_option) => {
				const label = _option === 'min' ? 'Min' : 'Max';
				return (
					<React.Fragment key={_option}>
						{label}
						<div className={styles['numeric-field__compound']} key={_option}>
							{elements.map((_compoundUnit) => renderSingularCompoundUnit(_compoundUnit, _option))}
						</div>
					</React.Fragment>
				);
			});
		}

		if (isCompoundUnit && !isRange) {
			const elements = Object.keys(CompoundUnitValueLimits[unit]);

			return (
				<div className={styles['numeric-field__compound']}>
					{elements.map((_compoundUnit) => renderSingularCompoundUnit(_compoundUnit))}
				</div>
			);
		}

		if (!isCompoundUnit && !isRange) {
			return (
				<NonCompoundField
					key={`${name}#${unit}`}
					name={name}
					onValueChange={onNonRangeValueChange}
					unit={unit}
					value={value}
				/>
			);
		}

	}, [isRange, name, onNonRangeValueChange, onRangeValueChange, renderSingularCompoundUnit, unit, value]);

	return (
		<div className={styles['numeric-field']} >
			<div className={styles['numeric-field__comparator']}>
				<Dropdown<ComparatorOption>
					defaultValue={comparatorValue}
					id="comparator-dropdown"
					label="Comparator"
					labelKey="label"
					onValueChange={onComparatorChange}
					options={COMPARATOR_OPTIONS}
					placeholder="Select Comparator"
					valueKey="id"
					withBorder={true}
					withCaret={true}
				/>
			</div>
			{comparator !== ComparatorType.EMPTY && renderValue()}
		</div>
	);
};

export default React.memo(NumericFieldFilter);
